import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/Pills";
import { ArrowUpRight, Filter } from "lucide-react";

export default function Requests() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const r = await api.get("/requests");
      setRequests(r.data.requests || []);
      setLoading(false);
    })();
  }, []);

  const filters = ["all", "approved_for_build", "awaiting_cto_review", "awaiting_test_evidence", "release_ready"];
  const visible = filter === "all" ? requests : requests.filter((r) => r.status === filter);

  return (
    <div data-testid="requests-page">
      <section className="bc-rise">
        <p className="eyebrow">Build requests</p>
        <h1 className="font-display text-[44px] tracking-tight leading-[1.05] mt-3">
          Every change, tracked end-to-end.
        </h1>
        <p className="mt-4 max-w-2xl text-[15px] text-[hsl(var(--bc-ink-soft))]">
          Each request carries its own scope, ownership, dependencies, advisory plan, evidence
          and receipt — without exposing raw code or private logs.
        </p>
      </section>

      <section className="mt-10 flex flex-wrap items-center gap-2 bc-rise bc-rise-d1" data-testid="filter-bar">
        <Filter size={14} className="text-[hsl(var(--bc-muted))] mr-1" />
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className="bc-status"
            data-tone={filter === f ? "signal" : "default"}
            data-testid={`filter-${f}`}
          >
            {f === "all" ? "All" : f.replace(/_/g, " ")}
          </button>
        ))}
      </section>

      <section className="mt-8 bc-card-flat divide-y divide-[hsl(var(--border))] bc-rise bc-rise-d2" data-testid="requests-list">
        {loading && <div className="p-6 text-sm text-[hsl(var(--bc-muted))]">Loading…</div>}
        {visible.map((r) => (
          <Link
            key={r.id}
            to={`/request/${r.request_id}`}
            className="grid grid-cols-12 gap-4 p-5 hover:bg-[hsl(var(--bc-paper))] transition-colors group"
            data-testid={`request-row-${r.request_id}`}
          >
            <div className="col-span-2 font-mono text-sm">{r.request_id}</div>
            <div className="col-span-5">
              <div className="text-[15px] font-medium">{r.requested_module}</div>
              <div className="text-xs text-[hsl(var(--bc-muted))] mt-1 line-clamp-1">{r.business_purpose}</div>
            </div>
            <div className="col-span-2 text-xs text-[hsl(var(--bc-ink-soft))]">
              <div>{r.estimated_effort_band}</div>
              <div className="text-[11px] text-[hsl(var(--bc-muted))]">{r.estimated_budget_band}</div>
            </div>
            <div className="col-span-2"><StatusPill status={r.status} /></div>
            <div className="col-span-1 flex justify-end text-[hsl(var(--bc-muted))] group-hover:text-[hsl(var(--bc-signal))]">
              <ArrowUpRight size={18} />
            </div>
          </Link>
        ))}
        {!loading && visible.length === 0 && <div className="p-6 text-sm text-[hsl(var(--bc-muted))]">No requests match this filter.</div>}
      </section>
    </div>
  );
}
